import type {
  CloseOrderRevisionSessionRequest,
  OrderRevisionBoardPageResponse,
  OrderRevisionRequestCategory,
  OrderRevisionSession,
  ReplyOrderRevisionRequestRequest,
} from "../../types/order-revision-request";
import type { CompanyOrderRevisionRequestRepository } from "../../../infrastructure/repositories/company/company-order-revision-request-repository-impl";

export class CompanyOrderRevisionRequestUseCase {
  constructor(private repository: CompanyOrderRevisionRequestRepository) {}

  /**
   * 세션 게시판 메인 (세션마다 1행)
   */
  async getOrderRevisionSessions(params: {
    companyId: number;
    category?: OrderRevisionRequestCategory;
    closed?: boolean;
    keyword?: string;
    page?: number;
    size?: number;
  }): Promise<OrderRevisionBoardPageResponse> {
    return this.repository.getOrderRevisionSessions(params.companyId, {
      category: params.category,
      closed: params.closed,
      keyword: params.keyword,
      page: params.page,
      size: params.size,
    });
  }

  /**
   * 세션 상세 — sessionId 기반 (채팅형, 메시지 시간순)
   */
  async getOrderRevisionSessionDetail(params: {
    companyId: number;
    sessionId: number;
  }): Promise<OrderRevisionSession> {
    return this.repository.getOrderRevisionSessionDetail(
      params.companyId,
      params.sessionId
    );
  }

  /**
   * 메시지 전송 — sessionId 기반, 활성 세션에만 가능
   */
  async sendAdminMessage(params: {
    companyId: number;
    sessionId: number;
    request: ReplyOrderRevisionRequestRequest;
  }): Promise<OrderRevisionSession> {
    return this.repository.sendAdminMessage(
      params.companyId,
      params.sessionId,
      params.request
    );
  }

  /**
   * 메시지 내용 수정
   */
  async updateAdminMessage(params: {
    companyId: number;
    messageId: number;
    request: ReplyOrderRevisionRequestRequest;
  }): Promise<OrderRevisionSession> {
    return this.repository.updateAdminMessage(
      params.companyId,
      params.messageId,
      params.request
    );
  }

  /**
   * 메시지 삭제
   */
  async deleteAdminMessage(params: {
    companyId: number;
    messageId: number;
  }): Promise<OrderRevisionSession> {
    return this.repository.deleteAdminMessage(
      params.companyId,
      params.messageId
    );
  }

  /**
   * 세션 종료/재작업 결정 — sessionId 기반.
   * needsRework=false면 "문의 종료", true면 "재작업 결정"
   */
  async closeSession(params: {
    companyId: number;
    sessionId: number;
    needsRework: boolean;
  }): Promise<OrderRevisionSession> {
    return this.repository.closeSession(params.companyId, params.sessionId, {
      needsRework: params.needsRework,
    } as CloseOrderRevisionSessionRequest);
  }

  /**
   * 세션 재오픈(되돌리기) — sessionId 기반.
   * 같은 주문에 활성 세션이 있으면 409 에러.
   */
  async reopenSession(params: {
    companyId: number;
    sessionId: number;
  }): Promise<OrderRevisionSession> {
    return this.repository.reopenSession(params.companyId, params.sessionId);
  }
}
